// Screen 15 · Home: the tiles that say what needs doing on the estimates, and the Pipeline (Iris's screens 1 and 3,
// 2026-10-19): on the desk and the tablet the board, every open lead on its step; on the phone, Needs you first, the
// board one tap away. The old step board over the job workflow is gone: a lead moves on the pipeline, a job's own
// checklist lives on the job (Jobs → Estimating).

import { Link } from 'react-router-dom';
import { rankNeedsYou } from '@zerogap/engine';
import { useInventoryNames } from './inventory/names';
import { useInventory } from '../lib/inventory';
import { useCrumb } from '../frame/Frame';
import { useRereadOnReturn, useRows } from '../lib/api';
import { useAllJobs } from '../lib/jobs';
import { PIPELINE_TABLES, usePipeline } from '../lib/pipeline';
import { useSession } from '../lib/session';
import { Head, Loading } from '../ui/kit';
import { Board } from './pipeline/Board';
import { NeedsYouList } from './pipeline/NeedsYou';

export function Home() {
  useCrumb([{ label: 'Home' }, { label: 'Pipeline' }]);
  const { user, device } = useSession();
  const names = useInventoryNames();
  const all = useAllJobs();
  const p = usePipeline();
  useRereadOnReturn(PIPELINE_TABLES);
  const engineering = useRows('engineering_request');
  const flags = useRows('variance_flag', { eq: { status: 'open' } });
  const changes = useRows('change_log', { eq: { status: 'new' } });
  const inv = useInventory();

  if (all.loading) return <Loading what="Loading the pipeline" />;

  // a retired job counts nowhere (decision 1, 2026-10-19)
  const open = all.jobs.filter((j) => !j.retired && ((j.opp?.outcome ?? 'open') === 'open' || j.opp?.outcome === 'won'));
  const unverified = open.reduce((n, j) => n + (j.res?.totals.unverified_items ?? 0), 0);
  const pass1 = open.filter((j) => j.itemCount > 0 && !j.job.pass1_completed_at);
  const pass2 = open.filter((j) => j.job.pass1_completed_at && !j.job.pass2_completed_at);
  const queue = engineering.rows.filter((e) => e.status !== 'verified').length;
  const reorder = inv.reorders.length + inv.waitingReorders.length;
  const needs = p.loading ? 0 : rankNeedsYou([...p.open, ...p.parked].map((lead) => ({ lead, needsOwner: lead.needsOwner, late: lead.late, quiet: lead.quiet, valueCents: lead.value.cents, likelihoodPct: lead.likelihood }))).length;
  const phone = device === 'phone';

  const tiles = (
    <div className="tiles">
      <Link className={`tile${needs ? ' soft' : ''}`} to="/pipeline/needs-you">
        <div className="big">{needs}</div>
        <small>{needs === 1 ? 'lead needs you' : 'leads need you'}</small>
      </Link>
      <Link className={`tile${unverified ? ' soft' : ''}`} to="/library">
        <div className="big">{unverified}</div>
        <small>unverified items in open estimates</small>
      </Link>
      <Link className={`tile${pass1.length ? ' soft' : ''}`} to={pass1[0] ? `/jobs/${pass1[0].job.id}?tab=review` : '/jobs'}>
        <div className="big">{pass1.length}</div>
        <small>{pass1.length === 1 ? `${pass1[0]!.opp?.name ?? ''} · pass 1 waiting` : 'jobs waiting on pass 1'}</small>
      </Link>
      <Link className="tile" to={pass2[0] ? `/jobs/${pass2[0].job.id}?tab=review` : '/jobs'}>
        <div className="big">{pass2.length}</div>
        <small>jobs waiting on pass 2</small>
      </Link>
      <Link className="tile" to="/library?view=engineering">
        <div className="big">{queue}</div>
        <small>in the engineering queue</small>
      </Link>
      <Link className={`tile${flags.rows.length ? ' soft' : ''}`} to="/shop?view=variance">
        <div className="big">{flags.rows.length}</div>
        <small>variances unanswered</small>
      </Link>
      <Link className={`tile${reorder ? ' soft' : ''}`} to="/inventory">
        <div className="big">{reorder}</div>
        <small>materials to reorder (under their {names.safety_stock})</small>
      </Link>
      <Link className="tile" to="/settings?view=changes">
        <div className="big">{changes.rows.length}</div>
        <small>new change-log entries</small>
      </Link>
    </div>
  );

  if (phone) {
    return (
      <div className="stack">
        <div className="head">
          <div>
            <small className="lb">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</small>
            <h1>
              Needs you · <span className="num">{needs}</span>
            </h1>
          </div>
          <div className="actions">
            <Link className="btn sm" to="/pipeline">
              Pipeline
            </Link>
          </div>
        </div>
        <NeedsYouList p={p} phone />
        {tiles}
      </div>
    );
  }

  return (
    <div className="stack">
      <Head title={`Urban Grain · ${new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`} sub={user?.name} />
      {tiles}
      <Board p={p} inHome />
    </div>
  );
}

/** The board on its own page (Home › Pipeline), the same on every device. */
export function PipelinePage() {
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline' }]);
  const p = usePipeline();
  useRereadOnReturn(PIPELINE_TABLES);
  return (
    <div className="stack">
      <Head title="Pipeline" sub="Where every open opportunity stands. Click a stage for its steps.">
        <Link className="btn sm" to="/pipeline/needs-you">
          Needs you
        </Link>
        <Link className="btn sm" to="/pipeline/won-lost">
          Won and lost
        </Link>
      </Head>
      <Board p={p} />
    </div>
  );
}
